import React, { useRef } from 'react';
import { X, Printer, Download, FileText, Target, CheckCircle, AlertTriangle, BookOpen } from 'lucide-react';
import { symptomes, signes, radios, urgenceConfig } from '../data/diagnosticData';

/**
 * Modal de prévisualisation et export PDF de la fiche diagnostic
 */
export default function DiagnosticPDF({
  isOpen,
  onClose,
  patientInfo,
  diagnosticResults,
  selectedSymptomes,
  selectedSignes,
  selectedRadios,
  itemLocalisations
}) {
  const printRef = useRef(null);

  if (!isOpen) return null;

  // Fonction d'impression (ouvre le dialogue d'impression du navigateur)
  const handlePrint = () => {
    const printContent = printRef.current;
    const originalContents = document.body.innerHTML;

    document.body.innerHTML = printContent.innerHTML;
    window.print();
    document.body.innerHTML = originalContents;
    window.location.reload();
  };

  // Téléchargement via impression en PDF
  const handleDownload = () => {
    handlePrint();
  };

  // Formater la date
  const formatDate = (dateStr) => {
    if (!dateStr) return new Date().toLocaleDateString('fr-FR');
    return new Date(dateStr).toLocaleDateString('fr-FR');
  };

  // Formater les dents concernées
  const formatTeeth = (teeth) => {
    if (!teeth || teeth.length === 0) return 'Non spécifié';
    if (teeth.length === 32) return 'Global (bouche entière)';
    return teeth.sort((a, b) => parseInt(a) - parseInt(b)).join(', ');
  };

  // Obtenir la couleur d'urgence
  const getUrgenceStyle = (urgence) => {
    const styles = {
      1: { bg: '#10B981', text: '#ECFDF5' },
      2: { bg: '#0EA5E9', text: '#F0F9FF' },
      3: { bg: '#F59E0B', text: '#FFFBEB' },
      4: { bg: '#F97316', text: '#FFF7ED' },
      5: { bg: '#EF4444', text: '#FEF2F2' },
      6: { bg: '#B91C1C', text: '#FEF2F2' }
    };
    return styles[urgence] || styles[1];
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[95vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-sky-500 to-violet-500 flex items-center justify-center">
              <FileText className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-800">Fiche Diagnostic</h2>
              <p className="text-xs text-slate-500">Prévisualisation et export</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handlePrint}
              className="flex items-center gap-2 px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors"
            >
              <Printer className="w-4 h-4" />
              Imprimer
            </button>
            <button
              onClick={handleDownload}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-sky-500 to-violet-500 text-white rounded-lg hover:from-sky-600 hover:to-violet-600 transition-colors"
            >
              <Download className="w-4 h-4" />
              Télécharger PDF
            </button>
            <button
              onClick={onClose}
              className="p-2 text-slate-400 hover:text-slate-600 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Preview Content */}
        <div className="flex-1 overflow-y-auto p-6 bg-slate-50">
          <div ref={printRef} className="bg-white rounded-lg shadow-sm border border-slate-200 p-8 max-w-3xl mx-auto">
            {/* Contenu imprimable */}
            <style>{`
              @media print {
                body { margin: 0; padding: 20px; font-family: 'Inter', sans-serif; }
                .no-print { display: none !important; }
                .page-break { page-break-before: always; }
              }
            `}</style>

            {/* En-tête */}
            <div style={{ borderBottom: '3px solid #0EA5E9', paddingBottom: '16px', marginBottom: '24px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <h1 style={{ fontSize: '24px', fontWeight: 'bold', color: '#0EA5E9', margin: 0 }}>
                    Hello Diagnostic
                  </h1>
                  <p style={{ fontSize: '12px', color: '#64748B', margin: '4px 0 0 0' }}>
                    Fiche d'aide au diagnostic dentaire
                  </p>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <p style={{ fontSize: '12px', color: '#64748B', margin: 0 }}>
                    Date: {formatDate(patientInfo?.date)}
                  </p>
                  {patientInfo?.praticien && (
                    <p style={{ fontSize: '12px', color: '#64748B', margin: '2px 0 0 0' }}>
                      Praticien: {patientInfo.praticien}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Informations patient */}
            {(patientInfo?.nom || patientInfo?.prenom) && (
              <div style={{
                backgroundColor: '#F8FAFC',
                border: '1px solid #E2E8F0',
                borderRadius: '8px',
                padding: '16px',
                marginBottom: '24px'
              }}>
                <h2 style={{ fontSize: '14px', fontWeight: '600', color: '#475569', margin: '0 0 8px 0', textTransform: 'uppercase' }}>
                  Patient
                </h2>
                <p style={{ fontSize: '16px', fontWeight: '500', color: '#1E293B', margin: 0 }}>
                  {patientInfo?.prenom} {patientInfo?.nom}
                </p>
              </div>
            )}

            {/* Éléments cliniques sélectionnés */}
            <div style={{ marginBottom: '24px' }}>
              <h2 style={{ fontSize: '14px', fontWeight: '600', color: '#475569', margin: '0 0 12px 0', textTransform: 'uppercase' }}>
                Éléments cliniques relevés
              </h2>

              {selectedSymptomes.length > 0 && (
                <div style={{ marginBottom: '12px' }}>
                  <h3 style={{ fontSize: '12px', fontWeight: '600', color: '#0EA5E9', margin: '0 0 6px 0' }}>
                    Symptômes ({selectedSymptomes.length})
                  </h3>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                    {selectedSymptomes.map(id => (
                      <span key={id} style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                        padding: '4px 8px',
                        backgroundColor: '#F0F9FF',
                        border: '1px solid #BAE6FD',
                        borderRadius: '4px',
                        fontSize: '11px',
                        color: '#0369A1'
                      }}>
                        {symptomes[id]?.icone} {symptomes[id]?.nom}
                        {itemLocalisations[id]?.length > 0 && (
                          <span style={{ color: '#0EA5E9', fontWeight: '500' }}>
                            ({formatTeeth(itemLocalisations[id])})
                          </span>
                        )}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {selectedSignes.length > 0 && (
                <div style={{ marginBottom: '12px' }}>
                  <h3 style={{ fontSize: '12px', fontWeight: '600', color: '#8B5CF6', margin: '0 0 6px 0' }}>
                    Signes cliniques ({selectedSignes.length})
                  </h3>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                    {selectedSignes.map(id => (
                      <span key={id} style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                        padding: '4px 8px',
                        backgroundColor: '#F5F3FF',
                        border: '1px solid #DDD6FE',
                        borderRadius: '4px',
                        fontSize: '11px',
                        color: '#6D28D9'
                      }}>
                        {signes[id]?.nom}
                        {itemLocalisations[id]?.length > 0 && (
                          <span style={{ color: '#8B5CF6', fontWeight: '500' }}>
                            ({formatTeeth(itemLocalisations[id])})
                          </span>
                        )}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {selectedRadios.length > 0 && (
                <div style={{ marginBottom: '12px' }}>
                  <h3 style={{ fontSize: '12px', fontWeight: '600', color: '#14B8A6', margin: '0 0 6px 0' }}>
                    Signes radiologiques ({selectedRadios.length})
                  </h3>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                    {selectedRadios.map(id => (
                      <span key={id} style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                        padding: '4px 8px',
                        backgroundColor: '#F0FDFA',
                        border: '1px solid #99F6E4',
                        borderRadius: '4px',
                        fontSize: '11px',
                        color: '#0F766E'
                      }}>
                        {radios[id]?.nom}
                        {itemLocalisations[id]?.length > 0 && (
                          <span style={{ color: '#14B8A6', fontWeight: '500' }}>
                            ({formatTeeth(itemLocalisations[id])})
                          </span>
                        )}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Hypothèses diagnostiques */}
            {diagnosticResults.length > 0 && (
              <div style={{ marginBottom: '24px' }}>
                <h2 style={{ fontSize: '14px', fontWeight: '600', color: '#475569', margin: '0 0 12px 0', textTransform: 'uppercase' }}>
                  Hypothèses diagnostiques
                </h2>

                {diagnosticResults.map((diag, index) => {
                  const urgStyle = getUrgenceStyle(diag.urgence);
                  const urgInfo = urgenceConfig[diag.urgence];

                  return (
                    <div key={diag.id} style={{
                      border: '1px solid #E2E8F0',
                      borderRadius: '8px',
                      marginBottom: '12px',
                      overflow: 'hidden'
                    }}>
                      {/* Header du diagnostic */}
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '12px 16px',
                        backgroundColor: diag.score >= 80 ? '#F0FDF4' : '#FFFFFF',
                        borderBottom: '1px solid #E2E8F0'
                      }}>
                        {/* Score */}
                        <div style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '50%',
                          backgroundColor: diag.score >= 80 ? '#10B981' : diag.score >= 60 ? '#F59E0B' : '#94A3B8',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontWeight: 'bold',
                          fontSize: '12px',
                          flexShrink: 0
                        }}>
                          {diag.score}%
                        </div>

                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                            <span style={{ fontWeight: '600', color: '#1E293B', fontSize: '14px' }}>
                              {index + 1}. {diag.nom}
                            </span>
                            {diag.fullMatch && (
                              <span style={{
                                padding: '2px 6px',
                                backgroundColor: '#D1FAE5',
                                color: '#059669',
                                borderRadius: '4px',
                                fontSize: '10px',
                                fontWeight: '500'
                              }}>
                                Match complet
                              </span>
                            )}
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px', flexWrap: 'wrap' }}>
                            <span style={{
                              padding: '2px 8px',
                              backgroundColor: urgStyle.bg,
                              color: 'white',
                              borderRadius: '4px',
                              fontSize: '10px',
                              fontWeight: '500'
                            }}>
                              {urgInfo?.label}
                            </span>
                            {diag.affectedTeeth?.length > 0 && (
                              <span style={{
                                padding: '2px 8px',
                                backgroundColor: '#F0F9FF',
                                color: '#0369A1',
                                borderRadius: '4px',
                                fontSize: '10px',
                                fontWeight: '500'
                              }}>
                                Dent(s): {formatTeeth(diag.affectedTeeth)}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Contenu du diagnostic */}
                      <div style={{ padding: '12px 16px' }}>
                        <p style={{ fontSize: '11px', color: '#64748B', margin: '0 0 12px 0' }}>
                          {diag.desc}
                        </p>

                        {/* Conduite à tenir */}
                        {diag.treatments?.length > 0 && (
                          <div style={{
                            backgroundColor: '#F5F3FF',
                            border: '1px solid #DDD6FE',
                            borderRadius: '6px',
                            padding: '10px 12px'
                          }}>
                            <h4 style={{
                              fontSize: '11px',
                              fontWeight: '600',
                              color: '#7C3AED',
                              margin: '0 0 6px 0',
                              textTransform: 'uppercase'
                            }}>
                              Conduite à tenir
                            </h4>
                            <ul style={{ margin: 0, paddingLeft: '16px' }}>
                              {diag.treatments.map((t, i) => (
                                <li key={i} style={{ fontSize: '11px', color: '#4C1D95', marginBottom: '2px' }}>
                                  {t}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Avertissement */}
            <div style={{
              backgroundColor: '#FEF3C7',
              border: '1px solid #FCD34D',
              borderRadius: '8px',
              padding: '12px 16px',
              marginTop: '24px'
            }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                <span style={{ fontSize: '16px' }}>⚠️</span>
                <div>
                  <h4 style={{ fontSize: '12px', fontWeight: '600', color: '#92400E', margin: '0 0 4px 0' }}>
                    Avertissement
                  </h4>
                  <p style={{ fontSize: '10px', color: '#78350F', margin: 0, lineHeight: '1.4' }}>
                    Ce document est une aide au diagnostic et ne remplace pas le jugement clinique du praticien.
                    Les hypothèses proposées doivent être confrontées à l'examen clinique complet et aux examens complémentaires appropriés.
                  </p>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div style={{
              marginTop: '24px',
              paddingTop: '16px',
              borderTop: '1px solid #E2E8F0',
              textAlign: 'center'
            }}>
              <p style={{ fontSize: '10px', color: '#94A3B8', margin: 0 }}>
                Document généré par Hello Diagnostic - {formatDate(new Date())}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
